import classNames from "classnames";
import React from "react";

import { Alert } from "./Alert";

const classNamePrefix = "Form";

type FormRootProps = React.FormHTMLAttributes<HTMLFormElement> & {
  actions: React.ReactNode;
  errorMessage?: React.ReactNode;
  reminder?: React.ReactNode;
  successMessage?: React.ReactNode;
};

const FormRoot = React.forwardRef(
  (props: FormRootProps, ref?: React.ForwardedRef<HTMLFormElement>) => {
    const {
      actions,
      children,
      className,
      errorMessage,
      noValidate = true,
      reminder,
      successMessage,
      ...restProps
    } = props;
    return (
      <form
        ref={ref}
        {...restProps}
        className={classNames(className, classNamePrefix)}
        noValidate={noValidate}
      >
        {children}
        {errorMessage && <Alert status="error">{errorMessage}</Alert>}
        {successMessage && <Alert status="success">{successMessage}</Alert>}
        {reminder && (
          <div className={`${classNamePrefix}-reminder`}>{reminder}</div>
        )}
        <div className={`${classNamePrefix}-actions`}>{actions}</div>
      </form>
    );
  },
);

type FormLegendProps = React.HTMLAttributes<HTMLLegendElement> & {
  icon?: React.ReactNode;
};

type FormFieldsetProps = React.FieldsetHTMLAttributes<HTMLFieldSetElement>;

const FormFieldset = React.forwardRef(
  (props: FormFieldsetProps, ref?: React.ForwardedRef<HTMLFieldSetElement>) => {
    const { className, ...restProps } = props;
    return (
      <fieldset
        ref={ref}
        {...restProps}
        className={classNames(className, `${classNamePrefix}-fieldset`)}
      />
    );
  },
);

const FormLegend = React.forwardRef(
  (props: FormLegendProps, ref?: React.ForwardedRef<HTMLLegendElement>) => {
    const { children, className, icon, ...restProps } = props;
    return (
      <legend
        ref={ref}
        {...restProps}
        className={classNames(className, `${classNamePrefix}-legend`)}
        data-icon={icon ? "true" : undefined}
      >
        {icon}
        {children}
      </legend>
    );
  },
);

export const Form = Object.assign(FormRoot, {
  Fieldset: FormFieldset,
  Legend: FormLegend,
});
